import { GetMainMenuItemsParams, IMenuActionParams, MenuItemDef } from '@ag-grid-community/core';
import { I18n } from 'common/i18n';
import { ForgeIcon } from '@tylertech/forge-react';
import ReactDOM from 'react-dom';
import React from 'react';
import { isUndefined } from 'lodash';
import { ViewColumn } from 'common/types/viewColumn';
import {
  CustomSortConfig,
  HierarchyColumnConfig,
  OrderConfig,
  SortDirection,
} from 'common/visualizations/vif';
import eventBus from './EventBus';
import { UniqueValuesData, TableColumnFormat } from '../types';
import {
  handleHeaderSortIndexForUnindented,
  HEADER_SORT_INDEX,
  updateMultipleSortConfig,
  updateSortConfig,
} from './SortHelpers';
import { GROUP_COLUMN_PREFIX } from '../Constants';

interface IGetCustomSortMenuItems {
  getMainMenuParams: GetMainMenuItemsParams;
  mainMenuItems?: (string | MenuItemDef)[];
  columns?: ViewColumn[];
  setIsCustomSortModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  hierarchies?: HierarchyColumnConfig[];
  selectedColumns?: ViewColumn[];
  setSelectedColumns?: React.Dispatch<React.SetStateAction<ViewColumn[]>>;
  getUniqueValues?: (
    columns: ViewColumn[],
    order: OrderConfig[],
    customSortConfig: CustomSortConfig | undefined,
  ) => Promise<UniqueValuesData>;
  vifOrderConfig?: OrderConfig[];
  columnFormats: { [key: string]: TableColumnFormat };
  hierarchyId: string | undefined;
  customSortConfig?: CustomSortConfig;
  isColumnDisabled: boolean;
  distinctValuesCountMap?: object;
  setCurrentSortState?: React.Dispatch<React.SetStateAction<OrderConfig[]>>;
  handleCustomSortCycle?: (newOrderConfig: OrderConfig[], hierarchyId?: string) => void;
  handleIndentedHeaderSortIndex?: (headerSortCycleIndex: number, hierarchyId?: string) => void;
}

const isIndentedHierarchy = (clickedColumnId: string) => {
  // Columns of indented hierarchies only contain the following column id
  return clickedColumnId === 'ag-grid-autocolumn';
};

const isUnindentedHierarchy = (clickedColumnId: string) => {
  // Columns of unindented hierarchies includes the following column id
  // e.g. `ag-grid-autocolumn-number` or `ag-grid-autocolumn-money`
  return clickedColumnId.includes('ag-grid-autocolumn-');
};

const getHierachiesAsViewColumns = (
  columns: ViewColumn[],
  hierarchies: HierarchyColumnConfig[],
): ViewColumn[] => {
  return hierarchies
    .map((hierarchy) => columns.find((column) => column.fieldName === hierarchy.columnName))
    .filter((column): column is ViewColumn => column !== undefined);
};

const getSelectedViewColumns = (columns: ViewColumn[], clickedColumnId: string) => {
  return columns.filter((column) => column.fieldName.toLocaleLowerCase() === clickedColumnId);
};

const getColumnsToCustomSort = (
  columns: ViewColumn[],
  hierarchies: HierarchyColumnConfig[],
  clickedColumnId: string,
) => {
  if (isIndentedHierarchy(clickedColumnId)) {
    return getHierachiesAsViewColumns(columns, hierarchies);
  }

  if (isUnindentedHierarchy(clickedColumnId)) {
    clickedColumnId = clickedColumnId.split('ag-grid-autocolumn-')[1];
  }

  return getSelectedViewColumns(columns, clickedColumnId);
};

const updateSortCycle = (
  updatedSortConfig: OrderConfig[],
  hierarchyId: string | undefined,
  setCurrentSortState: React.Dispatch<React.SetStateAction<OrderConfig[]>> | undefined,
  handleCustomSortCycle: ((newOrderConfig: OrderConfig[], hierarchyId?: string) => void) | undefined,
) => {
  if (!isUndefined(setCurrentSortState) && !isUndefined(handleCustomSortCycle)) {
    setCurrentSortState(updatedSortConfig);
    handleCustomSortCycle(updatedSortConfig, hierarchyId);
  }
};

const getUpdatedSortConfig = (
  colId: string,
  params: IMenuActionParams,
  vifOrderConfig: OrderConfig[],
  nextSort: SortDirection,
) => {
  const isMultiSort = false;
  let updatedSortConfig: OrderConfig[];
  if (isIndentedHierarchy(colId)) {
    const groupedColIds = params.context.groupedColumns;
    const columnsToUpdate = groupedColIds.map((columnName: string) => {
      return { columnName: columnName, nextSort };
    });
    updatedSortConfig = updateMultipleSortConfig({
      configs: vifOrderConfig,
      columnNames: columnsToUpdate,
      isMultiSort,
    });
  } else if (isUnindentedHierarchy(colId)) {
    const columnName = colId.replace(`${GROUP_COLUMN_PREFIX.toLowerCase()}-`, '');
    updatedSortConfig = updateSortConfig({
      configs: vifOrderConfig,
      columnName: columnName,
      nextSort,
      isMultiSort,
      useAscending: true,
    });
  } else {
    updatedSortConfig = updateSortConfig({
      configs: vifOrderConfig,
      columnName: colId,
      nextSort,
      isMultiSort,
      useAscending: true,
    });
  }
  return updatedSortConfig;
};

const groupedColumnsSortToFollow = (
  groupedColumnNames: string[],
  vifOrderConfig: OrderConfig[] | undefined,
) => {
  return groupedColumnNames.map((columnName: string) => {
    const matchedConfig = getMatchedConfig(vifOrderConfig, columnName);
    return matchedConfig?.sortToFollow;
  });
};

const getMatchedConfig = (vifOrderConfig: OrderConfig[] | undefined, columnName: string) => {
  return vifOrderConfig?.find((config) => config.columnName === columnName);
};

const getSortToFollow = (
  colId: string,
  getMainMenuParams: GetMainMenuItemsParams,
  vifOrderConfig: OrderConfig[] | undefined,
) => {
  let sortToFollow;
  if (isIndentedHierarchy(colId)) {
    const groupedColumnNames = getMainMenuParams.context.groupedColumns;
    const groupedSorting = groupedColumnsSortToFollow(groupedColumnNames, vifOrderConfig);
    const uniqueSortingValues = new Set(groupedSorting);
    if (groupedSorting.length == groupedColumnNames?.length && uniqueSortingValues.size == 1) {
      sortToFollow = groupedSorting[0];
    }
  } else if (isUnindentedHierarchy(colId)) {
    const columnName = colId.replace(`${GROUP_COLUMN_PREFIX.toLowerCase()}-`, '');
    const matchedConfig = getMatchedConfig(vifOrderConfig, columnName);
    sortToFollow = matchedConfig?.sortToFollow;
  } else {
    sortToFollow = vifOrderConfig?.find((config) => config.columnName === colId)?.sortToFollow;
  }
  return sortToFollow;
};

const updateHeaderSortIndex = (
  handleIndentedHeaderSortIndex: ((headerSortCycleIndex: number, hierarchyId?: string) => void) | undefined,
  colId: string,
  hierarchyId: string | undefined,
  hierarchies: HierarchyColumnConfig[] | undefined,
  updatedSortConfig: OrderConfig[],
  indentedSortIndex: number,
) => {
  if (isUndefined(handleIndentedHeaderSortIndex)) return;

  if (isIndentedHierarchy(colId)) {
    handleIndentedHeaderSortIndex(indentedSortIndex, hierarchyId);
  } else if (isUnindentedHierarchy(colId) && !isUndefined(hierarchies)) {
    handleHeaderSortIndexForUnindented({
      hierarchies,
      orderConfig: updatedSortConfig,
      hierarchyId,
      handleIndentedHeaderSortIndex,
    });
  }
};

const getCustomSortMenuItems = ({
  getMainMenuParams,
  mainMenuItems = [],
  columns,
  hierarchies,
  selectedColumns,
  setIsCustomSortModalOpen,
  setSelectedColumns,
  getUniqueValues,
  vifOrderConfig,
  columnFormats,
  hierarchyId,
  customSortConfig,
  isColumnDisabled,
  distinctValuesCountMap,
  setCurrentSortState,
  handleCustomSortCycle,
  handleIndentedHeaderSortIndex,
}: IGetCustomSortMenuItems) => {
  if (mainMenuItems === undefined || mainMenuItems?.length > 0) mainMenuItems = [];

  const colId = getMainMenuParams.column.getColId().toLowerCase();

  const sortAscItem = {
    name: I18n.t('common.ag_grid_react.sortAscending'),
    action: (params: IMenuActionParams) => {
      if (!params.column || !vifOrderConfig) return;

      const updatedSortConfig = getUpdatedSortConfig(colId, params, vifOrderConfig, 'ascending');
      updateSortCycle(updatedSortConfig, hierarchyId, setCurrentSortState, handleCustomSortCycle);
      updateHeaderSortIndex(
        handleIndentedHeaderSortIndex,
        colId,
        hierarchyId,
        hierarchies,
        updatedSortConfig,
        HEADER_SORT_INDEX.ASCENDING,
      );
    },
    icon: (() => {
      const container = document.createElement('div');
      ReactDOM.render(<ForgeIcon external external-type="standard" name="arrow_upward" />, container);
      return container;
    })(),
  };

  const sortDescItem = {
    name: I18n.t('common.ag_grid_react.sortDescending'),
    action: (params: IMenuActionParams) => {
      if (!params.column || !vifOrderConfig) return;

      const updatedSortConfig = getUpdatedSortConfig(colId, params, vifOrderConfig, 'descending');
      updateSortCycle(updatedSortConfig, hierarchyId, setCurrentSortState, handleCustomSortCycle);
      updateHeaderSortIndex(
        handleIndentedHeaderSortIndex,
        colId,
        hierarchyId,
        hierarchies,
        updatedSortConfig,
        HEADER_SORT_INDEX.DESCENDING,
      );
    },
    icon: (() => {
      const container = document.createElement('div');
      ReactDOM.render(<ForgeIcon external external-type="standard" name="arrow_downward" />, container);
      return container;
    })(),
  };

  const customSortItem = {
    name: I18n.t('common.ag_grid_react.customSort'),
    action: (params: IMenuActionParams) => {
      if (
        !columns ||
        hierarchies === undefined ||
        !selectedColumns ||
        !setIsCustomSortModalOpen ||
        !params.column ||
        !setSelectedColumns ||
        !vifOrderConfig
      ) {
        return;
      }

      hierarchies = hierarchies.filter((hierarchy) => hierarchy.isGrouping);
      const clickedColumnId: string = params.column.getColId().toLocaleLowerCase();
      const columnsToColumnSort = getColumnsToCustomSort(columns, hierarchies, clickedColumnId);
      const isClickedColumnAGrouping = clickedColumnId.includes(GROUP_COLUMN_PREFIX.toLocaleLowerCase());
      eventBus.dispatch('customSort', {
        columnsToSort: columnsToColumnSort,
        columnFormats: columnFormats,
        vifOrderConfig: vifOrderConfig,
        getUniqueValues: getUniqueValues,
        hierarchyId: hierarchyId,
        customSortConfig: customSortConfig,
        distinctValuesCountMap,
        isClickedColumnAGrouping,
      });
    },
    icon: (() => {
      const container = document.createElement('div');
      ReactDOM.render(<ForgeIcon external external-type="standard" name="swap_vert" />, container);
      return container;
    })(),
    disabled: isColumnDisabled,
    tooltip: isColumnDisabled
      ? I18n.t('shared.components.custom_sort.modal.rows_exceeded_tooltip')
      : undefined,
  };

  const sortToFollow = getSortToFollow(colId, getMainMenuParams, vifOrderConfig);
  if (sortToFollow === 'ascending') {
    mainMenuItems.push(sortDescItem);
  } else if (sortToFollow === 'descending') {
    mainMenuItems.push(sortAscItem);
  } else {
    mainMenuItems.push(sortAscItem, sortDescItem);
  }

  mainMenuItems.push(customSortItem);

  return mainMenuItems;
};

export default getCustomSortMenuItems;
